"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useRevieve = void 0;
var _environment = require("@ori/environment");
var _presentationUtils = require("@ori/presentation-utils");
var _react = require("react");
var _constants = require("../constants");
var _contexts = require("../contexts");
var _models = require("../models");
var _utils = require("../utils");
var _useLocale = require("./useLocale");
var _useRedirectToPdp = require("./useRedirectToPdp");
var _useRevieveAdvisor = require("./useRevieveAdvisor");
var _useSessionStorage = require("./useSessionStorage");
const useRevieve = ({
  configOverrides,
  experienceId,
  partnerId,
  placement,
  queryParam,
  recommendationPageEnabled,
  renderTo,
  onClose,
  onOpen
}) => {
  const {
    environment
  } = (0, _environment.useEnvironment)();
  const env = environment === 'live' ? 'prod' : 'test';
  const {
    openQueryParam
  } = (0, _contexts.useNovageSkincareAdvisor)();
  const canChangeOpenQueryParam = openQueryParam !== _constants.OPEN_QUERY_PARAM_ALWAYS;
  const redirectToPdp = (0, _useRedirectToPdp.useRedirectToPdp)({
    placement
  });
  const locale = (0, _useLocale.useLocale)();
  const [state, setState] = (0, _react.useState)(_constants.STATE_DEFAULT);
  const {
    clear,
    restoreProduct,
    storeProduct
  } = (0, _useSessionStorage.useSessionStorage)({
    environment: env,
    partnerId
  });
  const {
    close,
    open,
    reset
  } = (0, _useRevieveAdvisor.useRevieveAdvisor)({
    clear
  });
  const clearOpenQueryParam = (0, _react.useCallback)(() => {
    if (canChangeOpenQueryParam) {
      (0, _utils.removeOpenQueryParam)(queryParam);
    }
  }, [canChangeOpenQueryParam, queryParam]);
  const setupOpenQueryParam = (0, _react.useCallback)(() => {
    if (canChangeOpenQueryParam) {
      (0, _utils.setOpenQueryParam)(queryParam);
    }
  }, [canChangeOpenQueryParam, queryParam]);
  const continueToPdp = (0, _react.useCallback)(() => {
    const product = restoreProduct();
    if (product) {
      redirectToPdp({
        product
      });
    }
  }, [redirectToPdp, restoreProduct]);
  const handleClose = (0, _react.useCallback)(() => {
    setState(_constants.STATE_DEFAULT);
    clearOpenQueryParam();
    onClose?.();
  }, [clearOpenQueryParam, onClose]);
  const handleOpen = (0, _react.useCallback)(() => {
    setupOpenQueryParam();
    onOpen?.();
    if (restoreProduct()) {
      setState(_constants.STATE_RESTORED);
    } else {
      open();
    }
  }, [setupOpenQueryParam, onOpen, restoreProduct, open]);
  const initConfig = (0, _react.useMemo)(() => {
    const disableAnalytics = !(0, _utils.isAnalyticsConsentGranted)();
    return {
      env,
      locale,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- 3rd party naming
      partner_id: partnerId,
      experienceId,
      analytics: {
        disableUsageAnalytics: disableAnalytics,
        disableProductAnalytics: disableAnalytics,
        disableErrorReporting: disableAnalytics
      },
      renderTo,
      onClose: handleClose,
      onAddToCart: data => {
        if (Array.isArray(data)) {
          data.forEach(product => (0, _utils.addToBasket)({
            placement,
            product
          }));
        } else {
          (0, _utils.addToBasket)({
            placement,
            product: data
          });
        }
      },
      onClickProduct: (product, source) => {
        if (source === _models.ClickProductSource.productList || source === _models.ClickProductSource.expandedProductCard) {
          setupOpenQueryParam();
          storeProduct(product);
          redirectToPdp({
            product,
            newTab: true
          });
        }
      },
      onCheckout: () => {
        const url = (0, _presentationUtils.getCurrentUrl)();
        url.pathname = '/shopping/basket';
        url.searchParams.delete(queryParam);
        window.location.assign(url);
      },
      onDisplayProducts: (historyId, products) => {
        if (recommendationPageEnabled) {
          return;
        }
        const product = products[0];
        if (product) {
          setupOpenQueryParam();
          storeProduct(product);
          redirectToPdp({
            product,
            delay: 2000
          });
        }
      },
      onTrackingEvent: _utils.sendUserEvents,
      onFetchProducts: () => {
        if (recommendationPageEnabled) {
          return;
        }
        close();
        setState(_constants.STATE_LOADING);
      },
      ...configOverrides
    };
  }, [close, configOverrides, env, experienceId, handleClose, locale, partnerId, placement, queryParam, recommendationPageEnabled, redirectToPdp, renderTo, setupOpenQueryParam, storeProduct]);
  const onInit = (0, _react.useCallback)(() => {
    if (openQueryParam) {
      handleOpen();
    }
  }, [handleOpen, openQueryParam]);
  const handleReset = (0, _react.useCallback)(() => {
    setState(_constants.STATE_DEFAULT);
    reset(initConfig);
    handleOpen();
  }, [handleOpen, initConfig, reset]);
  return (0, _react.useMemo)(() => ({
    close: handleClose,
    continueToPdp,
    initConfig,
    onInit,
    open: handleOpen,
    reset: handleReset,
    state
  }), [continueToPdp, handleClose, handleOpen, handleReset, initConfig, onInit, state]);
};
exports.useRevieve = useRevieve;
